/**
 * TripRealtimeContext - Provides the current trip's WebSocket subscribe API
 * to descendant hooks/components without prop-drilling tripId everywhere.
 * Mounted per-page (TripDetailPage/MembersPage/PollsPage/
 * PollDetailPage) — see T035.
 */

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useTripRealtime } from '../hooks/useTripRealtime';
import type { RealtimeEventName, RealtimeEventPayloadMap } from '../utils/realtimeEvents';

interface TripRealtimeContextValue {
  subscribe: <E extends RealtimeEventName>(
    event: E,
    callback: (data: RealtimeEventPayloadMap[E]) => void
  ) => () => void;
}

const TripRealtimeContext = createContext<TripRealtimeContextValue | undefined>(undefined);

export function TripRealtimeProvider({ tripId, children }: { tripId: string; children: ReactNode }) {
  const { subscribe } = useTripRealtime(tripId);
  return <TripRealtimeContext.Provider value={{ subscribe }}>{children}</TripRealtimeContext.Provider>;
}

/**
 * Subscribe to a realtime event for the lifetime of the calling component.
 * A no-op when called outside a TripRealtimeProvider (e.g. a page that
 * hasn't mounted realtime yet) rather than throwing, so data hooks like
 * useTripMembers/usePolls can call this unconditionally.
 */
export function useTripRealtimeSubscribe<E extends RealtimeEventName>(
  event: E,
  callback: (data: RealtimeEventPayloadMap[E]) => void
) {
  const context = useContext(TripRealtimeContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!context) return;
    return context.subscribe(event, (data) => callbackRef.current(data));
  }, [context, event]);
}
